import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import LegalLanguageMenu from './LegalLanguageMenu';

const content = {
  it: {
    updated: 'Ultimo aggiornamento: 4 maggio 2026',
    kicker: 'Cookie e storage',
    title: 'Cookie e storage locale',
    intro: 'Outfit Detective non usa cookie di marketing o tracciamento. Usiamo solo dati tecnici nel browser per far funzionare il servizio.',
    sections: [
      ['Cosa salviamo', 'Nel localStorage del browser salviamo token di autenticazione, refresh token, lingua scelta e preferenze richieste dall utente. Alcune pagine usano sessionStorage per cache temporanee e posizione di scorrimento. Questi dati servono per mantenere la sessione attiva e migliorare funzioni essenziali dell app.'],
      ['Cookie tecnici', 'Se in futuro useremo cookie tecnici strettamente necessari, li useremo solo per sicurezza, login o funzioni richieste dall utente. Non richiedono consenso separato.'],
      ['Nessun tracking', 'Non usiamo cookie pubblicitari, pixel social, heatmap o analytics di terze parti senza consenso. Se aggiungeremo strumenti non necessari, introdurremo una gestione del consenso dedicata.'],
      ['Come cancellarli', 'Puoi cancellare i dati locali facendo logout, cancellando l account o svuotando dati sito e localStorage dalle impostazioni del browser.'],
    ],
  },
  en: {
    updated: 'Last updated: May 4, 2026',
    kicker: 'Cookies and storage',
    title: 'Cookies and local storage',
    intro: 'Outfit Detective does not use marketing or tracking cookies. We only use technical browser storage to provide the service.',
    sections: [
      ['What we store', 'In browser localStorage we store authentication tokens, refresh tokens, your selected language, and user-requested preferences. Some pages use sessionStorage for temporary caches and scroll position. These data keep your session active and support essential app features.'],
      ['Technical cookies', 'If we use strictly necessary technical cookies in the future, they will be used only for security, login, or features requested by the user. They do not require separate consent.'],
      ['No tracking', 'We do not use advertising cookies, social pixels, heatmaps, or third-party analytics without consent. If we add non-essential tools, we will introduce dedicated consent controls.'],
      ['How to delete them', 'You can remove local data by logging out, deleting your account, or clearing site data and localStorage from your browser settings.'],
    ],
  },
  de: {
    updated: 'Zuletzt aktualisiert: 4. Mai 2026',
    kicker: 'Cookies und Speicher',
    title: 'Cookies und lokaler Speicher',
    intro: 'Outfit Detective nutzt keine Marketing- oder Tracking-Cookies. Wir verwenden nur technischen Browser-Speicher, damit der Dienst funktioniert.',
    sections: [
      ['Was wir speichern', 'Im localStorage des Browsers speichern wir Authentifizierungs-Token, Refresh-Token und die gewählte Sprache. Diese Daten halten die Sitzung aktiv und zeigen die Seite in deiner Sprache.'],
      ['Technische Cookies', 'Falls wir künftig strikt notwendige technische Cookies verwenden, dienen sie nur Sicherheit, Login oder vom Nutzer angeforderten Funktionen. Dafür ist keine separate Einwilligung nötig.'],
      ['Kein Tracking', 'Wir nutzen ohne Einwilligung keine Werbe-Cookies, Social-Pixel, Heatmaps oder Drittanbieter-Analytics. Wenn wir nicht notwendige Tools hinzufügen, führen wir eine eigene Einwilligungssteuerung ein.'],
      ['So löschst du sie', 'Du kannst lokale Daten entfernen, indem du dich abmeldest, dein Konto löschst oder Websitedaten und localStorage in den Browser-Einstellungen leerst.'],
    ],
  },
  fr: {
    updated: 'Dernière mise à jour : 4 mai 2026',
    kicker: 'Cookies et stockage',
    title: 'Cookies et stockage local',
    intro: "Outfit Detective n'utilise pas de cookies marketing ou de suivi. Nous utilisons uniquement le stockage technique du navigateur pour fournir le service.",
    sections: [
      ['Ce que nous stockons', "Dans le localStorage du navigateur, nous stockons les jetons d'authentification, les refresh tokens et la langue choisie. Ces données maintiennent la session active et affichent le site dans la langue sélectionnée."],
      ['Cookies techniques', "Si nous utilisons à l'avenir des cookies techniques strictement nécessaires, ils serviront uniquement à la sécurité, au login ou aux fonctions demandées par l'utilisateur. Ils ne nécessitent pas de consentement séparé."],
      ['Aucun tracking', "Nous n'utilisons pas de cookies publicitaires, pixels sociaux, heatmaps ou analytics tiers sans consentement. Si nous ajoutons des outils non essentiels, nous introduirons des contrôles de consentement dédiés."],
      ['Comment les supprimer', "Vous pouvez supprimer les données locales en vous déconnectant, en supprimant votre compte ou en effaçant les données du site et le localStorage dans les paramètres du navigateur."],
    ],
  },
  ro: {
    updated: 'Ultima actualizare: 4 mai 2026',
    kicker: 'Cookie-uri si stocare',
    title: 'Cookie-uri si stocare locala',
    intro: 'Outfit Detective nu foloseste cookie-uri de marketing sau tracking. Folosim doar stocare tehnica in browser pentru a furniza serviciul.',
    sections: [
      ['Ce stocam', 'In localStorage-ul browserului stocam tokenuri de autentificare, refresh tokenuri si limba aleasa. Aceste date mentin sesiunea activa si afiseaza site-ul in limba selectata.'],
      ['Cookie-uri tehnice', 'Daca vom folosi in viitor cookie-uri tehnice strict necesare, acestea vor fi folosite doar pentru securitate, login sau functionalitati cerute de utilizator. Nu necesita consimtamant separat.'],
      ['Fara tracking', 'Nu folosim cookie-uri publicitare, pixeli social, heatmap-uri sau analytics de la terti fara consimtamant. Daca adaugam instrumente neesentiale, vom introduce controale dedicate de consimtamant.'],
      ['Cum le stergi', 'Poti sterge datele locale prin logout, stergerea contului sau stergerea datelor site-ului si localStorage din setarile browserului.'],
    ],
  },
  es: {
    updated: 'Última actualización: 4 de mayo de 2026',
    kicker: 'Cookies y almacenamiento',
    title: 'Cookies y almacenamiento local',
    intro: 'Outfit Detective no usa cookies de marketing ni seguimiento. Solo usamos almacenamiento técnico del navegador para prestar el servicio.',
    sections: [
      ['Qué guardamos', 'En el localStorage del navegador guardamos tokens de autenticación, refresh tokens y el idioma elegido. Estos datos mantienen la sesión activa y muestran el sitio en tu idioma.'],
      ['Cookies técnicas', 'Si en el futuro usamos cookies técnicas estrictamente necesarias, serán solo para seguridad, login o funciones solicitadas por el usuario. No requieren consentimiento separado.'],
      ['Sin tracking', 'No usamos cookies publicitarias, píxeles sociales, heatmaps ni analytics de terceros sin consentimiento. Si añadimos herramientas no esenciales, incorporaremos controles de consentimiento dedicados.'],
      ['Cómo eliminarlos', 'Puedes eliminar los datos locales cerrando sesión, eliminando tu cuenta o borrando datos del sitio y localStorage desde la configuración del navegador.'],
    ],
  },
};

function CookieStorageContent({ c }) {
  return (
    <main className="ct-legal-main">
      <article className="ct-legal-doc">
        <div className="ct-legal-head">
          <p className="ct-legal-kicker">{c.kicker}</p>
          <h1>{c.title}</h1>
          <p className="ct-legal-meta">{c.updated}</p>
          <p className="ct-legal-intro-text">{c.intro}</p>
        </div>

        {c.sections.map(([title, body]) => (
          <section className="ct-legal-section" key={title}>
            <h2>{title}</h2>
            <p>{body}</p>
          </section>
        ))}
      </article>
    </main>
  );
}

export default function CookieStorage({ compact = false }) {
  const { language } = useI18n();
  const C = content[language] || content.en;

  if (compact) {
    return <CookieStorageContent c={C} />;
  }

  return (
    <main className="ct-legal-page">
      <header className="ct-legal-header">
        <Link to="/" className="ct-legal-brand">
          <span className="ct-brand-mark image">
            <img src="/brand/outfit-detective-mark-transparent.svg" alt="" />
          </span>
          <span>Outfit Detective</span>
        </Link>
        <LegalLanguageMenu />
      </header>
      <CookieStorageContent c={C} />
    </main>
  );
}
